<template>
  <div class="teaching-log-detail">
    <div v-if="loading" class="loading-container">
      <van-loading type="spinner" color="#1677ff" size="24px" />
      <p class="loading-text">加载中...</p>
    </div>

    <div v-else class="detail-content">
      <div class="course-header">
        <div class="course-info">
          <h1 class="course-title">{{ detail.courseName }}</h1>
          <div class="course-meta">
            <span class="meta-tag">{{ subjectName }}</span>
            <span class="meta-tag">{{ gradeName }}</span>
          </div>
          <div class="course-time">
            <van-icon name="clock-o" size="14" />
            <span>{{ formatDateTime(detail.startDateTime) }} - {{ formatTime(detail.endDateTime) }}</span>
          </div>
        </div>
        <div class="course-status" :class="statusClass">
          {{ statusText }}
        </div>
      </div>

      <div class="info-card">
        <div class="info-row">
          <div class="info-item">
            <span class="label">任课老师：</span>
            <span class="value">{{ detail.teacherName }}</span>
          </div>
          <div class="info-item">
            <span class="label">时长：</span>
            <span class="value">{{ detail.classHour }}小时</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-item">
            <span class="label">是否跨校区：</span>
            <span class="value">{{ detail.isTransCampus ? '是' : '否' }}</span>
          </div>
          <div class="info-item">
            <span class="label">校区：</span>
            <span class="value">{{ detail.schoolName || '-' }}</span>
          </div>
        </div>

        <div class="info-row">
          <div class="info-item">
            <span class="label">加班课时：</span>
            <span class="value">{{ detail.overClassHour }}</span>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-header">
          <span class="section-title">👥 学生签到情况</span>
          <span class="section-badge">{{ detail.studentScheduleList.length }}人</span>
        </div>
        <div class="student-table">
          <div class="table-header">
            <span>学员</span>
            <span>学号</span>
            <span>课销金额</span>
            <span>签到状态</span>
          </div>
          <div class="table-body">
            <div v-for="student in detail.studentScheduleList" :key="student.studentScheduleId" class="table-row">
              <span class="student-name">{{ student.studentName }}</span>
              <span>{{ student.studentNo }}</span>
              <span>{{ student.classHourPrice }}</span>
              <span :class="getSignStatusClass(student.signStatus)">{{ getSignStatusText(student.signStatus) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-header">
          <span class="section-title">📝 上课内容</span>
        </div>
        <div class="section-content">
          <p class="text-content">{{ detail.classContent || '暂无内容' }}</p>
        </div>
      </div>

      <div class="section">
        <div class="section-header">
          <span class="section-title">📋 备注</span>
        </div>
        <div class="section-content">
          <p class="text-content">{{ detail.courseEvaluation || '暂无备注' }}</p>
        </div>
      </div>


      <!-- 
      <div class="section">
        <div class="section-header">
          <span class="section-title">📊 课时消耗明细</span>
        </div>
        <div class="student-table">
          <div class="table-header">
            <span>学员</span>
            <span>课程名称</span>
            <span>收据号</span>
            <span>购买课时</span>
            <span>赠送课时</span>
            <span>课销金额</span>
          </div>
          <div class="table-body">
            <div v-for="(item, idx) in detail.studentScheduleClassHourList" :key="idx" class="table-row">
              <span class="student-name">{{ item.studentName }}</span>
              <span>{{ item.courseName }}</span>
              <span>{{ item.receiptNo }}</span>
              <span>{{ item.buyClassHour }}</span>
              <span>{{ item.giftClassHour }}</span>
              <span>{{ item.saleAmount }}</span>
            </div>
          </div>
        </div>
      </div> -->
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getAttendanceDetailByPlanIdForH5 } from '@/api/schedule'
import { useDict, selectDictLabel } from '@/utils/dict'
import { formatDateTime, formatTime } from '@/utils/common'

const route = useRoute()
const loading = ref(true)
const detail = ref({})

const dicts = useDict(
  'grade_list',
  'course_subject',
  'sign_status',
  'attendance_status'
)

const subjectName = computed(() => selectDictLabel(dicts.course_subject.value, detail.value.subject))
const gradeName = computed(() => selectDictLabel(dicts.grade_list.value, detail.value.grade))

const statusText = computed(() => selectDictLabel(dicts.attendance_status.value, detail.value.status))

const statusClassMap = {
  1: 'pending',
  2: 'completed'
}

const statusClass = computed(() => `status-${statusClassMap[detail.value.status] || ''}`)

function getSignStatusText(status) {
  return selectDictLabel(dicts.sign_status.value, status)
}

function getSignStatusClass(status) {
  return `sign-${statusClassMap[status] || ''}`
}

onMounted(async () => {
  const schedulePlanId = route.query.schedulePlanId
  if (!schedulePlanId) return

  try {
    await dicts.loadDicts()
    const response = await getAttendanceDetailByPlanIdForH5(schedulePlanId)
    detail.value = response
  } catch (error) {
    console.error('获取上课详情失败:', error)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.teaching-log-detail {
  min-height: 100vh;
  background-color: #f5f7fa;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f5f7fa;
}

.loading-text {
  margin-top: 12px;
  color: #999;
  font-size: 14px;
}

.detail-content {
  padding: 16px;
  padding-bottom: calc(80px + env(safe-area-inset-bottom));
}

.course-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: linear-gradient(135deg, #1677ff 0%, #4096ff 100%);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: #fff;
}

.course-title {
  margin: 0 0 10px 0;
  font-size: 20px;
  font-weight: 600;
}

.course-meta {
  display: flex;
  gap: 8px;
}

.meta-tag {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
}

.course-time {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  font-size: 13px;
  opacity: 0.9;
}

.course-status {
  padding: 6px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.status-pending {
  background: rgba(255, 255, 255, 0.2);
}

.status-completed {
  background: rgba(0, 200, 83, 0.3);
}

.info-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.info-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-item {
  flex: 0 0 50%;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item .label {
  font-size: 13px;
  color: #999;
  flex-shrink: 0;
  width: 88px;
}

.info-item .value {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.section {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.section-badge {
  background: #e6f4ff;
  color: #1677ff;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 12px;
}

.section-content {
  padding: 8px 0;
}

.text-content {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.student-table {
  width: 100%;
  overflow-x: auto;
}

.table-header {
  display: flex;
  background: #f5f7fa;
  padding: 12px 8px;
  border-radius: 8px;
  font-size: 12px;
  color: #999;
  font-weight: 500;
}

.table-header span {
  flex: 1;
  text-align: center;
}

.table-body {
  margin-top: 8px;
}

.table-row {
  display: flex;
  padding: 12px 8px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  color: #333;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row span {
  flex: 1;
  text-align: center;
}

.table-row .student-name {
  font-weight: 500;
}

.sign-pending {
  color: #999;
}

.sign-late {
  color: #faad14;
}

.sign-attended {
  color: #52c41a;
}

.sign-early {
  color: #faad14;
}

.sign-absent {
  color: #ff4d4f;
}
</style>
